import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";

// Manage which clients get the GBE logo on their printed asset tags.
// Unchecked = logo suppressed for that client (e.g. CDMN).
export function TagLogoSettingsDialog({ open, onClose }) {
  const [clients, setClients] = useState([]);
  const [suppressed, setSuppressed] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setQ("");
    api.get("/field/label-logo-settings")
      .then((r) => { setClients(r.data.clients || []); setSuppressed(new Set(r.data.suppressed_clients || [])); })
      .catch(() => toast.error("Could not load logo settings"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(
    () => clients.filter((c) => c.toLowerCase().includes(q.toLowerCase())), [clients, q]);

  const toggle = (c) => {
    setSuppressed((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/field/label-logo-settings", { suppressed_clients: Array.from(suppressed) });
      toast.success("Tag logo settings saved");
      onClose();
    } catch { toast.error("Could not save settings"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tag-logo-dialog">
        <DialogHeader><DialogTitle>Logo on printed asset tags</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">
          The GBE logo prints on every asset tag by default. Untick a client to leave the logo off their tags.
        </p>
        <div className="relative mt-1">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…"
                 className="pl-9 h-9 bg-slate-50" data-testid="tag-logo-search" />
        </div>
        <div className="mt-2 max-h-72 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50">
          {loading ? <div className="p-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
            : filtered.length === 0 ? <div className="p-4 text-center text-xs text-slate-400">No clients</div>
            : filtered.map((c) => (
              <label key={c} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
                     data-testid={`tag-logo-row-${c.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}`}>
                <input type="checkbox" checked={!suppressed.has(c)} onChange={() => toggle(c)} />
                <span className={suppressed.has(c) ? "text-slate-400 line-through" : "text-slate-700"}>{c}</span>
                {suppressed.has(c) && <span className="ml-auto text-[10px] text-amber-600">no logo</span>}
              </label>
            ))}
        </div>
        <div className="flex justify-between items-center mt-2">
          <span className="text-[11px] text-slate-400">{suppressed.size} client(s) without logo</span>
          <Button size="sm" onClick={save} disabled={busy} data-testid="tag-logo-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
